"use client";

import { useEffect, useRef, useMemo } from "react";
import { useStatsigClient } from "@statsig/react-bindings";
import { runStatsigSessionReplay } from "@statsig/session-replay";
import { runStatsigAutoCapture } from "@statsig/web-analytics";
import {
  useInitialContext,
  DefaultInitialContext,
  EXTERNAL_ACCOUNT_ID_COOKIE_NAME,
} from "@repo/sdk";
import useRoutePathname from "../hooks/use-route-pathname";
import useImpressionLogger from "../hooks/use-impression-logger";
import { useOnVisible } from "../hooks/use-on-visible";
import { markPageVisible } from "../visibility-gate";

/**
 * Syncs user details from initial context to Statsig, logs page views
 * and initializes session replay and autocapture in production.
 *
 * Note: Must be wrapped in Suspense - on client, useInitialContext throws (suspends)
 * when context is unavailable. On SSR, the hook returns null which we handle inline.
 */
export function StatsigObserver() {
  const initialContext = useInitialContext<DefaultInitialContext | null>();
  const { client } = useStatsigClient();

  // We have to have this useRef because runStatsigSessionReplay is not idempotent
  // so we have to track if we've already initialized it.
  const hasInitializedExtras = useRef(false);

  const user = useMemo(
    () => ({
      userID: initialContext?.account?.id,
      email: initialContext?.account?.email,
    }),
    [initialContext?.account?.id, initialContext?.account?.email]
  );

  // On SSR, useInitialContext returns null (doesn't throw). This check handles that case.
  // On client, useInitialContext throws (suspends) so this check is never reached - Suspense handles it.
  if (!initialContext) {
    return null;
  }

  // 1. Attach userID (and email when present) to the Statsig user, preserving
  //    whatever the SDK and StatsigUserPropertiesSync have already written.
  //    Owlet's platform handoff doesn't always populate
  //    `initialContext.account.email`; gating on email previously left those
  //    sessions firing without a bound userID. The server-side
  //    StatsigProvider boot in #932 covers the happy path, but this client
  //    path is still the fallback for refresh-issued tokens (which strip the
  //    `user_email` JWT claim) and pre-auth cold starts. Spreading
  //    `currentUser` is required so a later flush from
  //    `useStatsigUserProperties` doesn't get clobbered.
  useEffect(() => {
    if (!user.userID || !client) return;
    const currentUser = client.getContext().user;
    client.updateUserSync({
      ...currentUser,
      userID: user.userID,
      ...(user.email ? { email: user.email } : {}),
    });
  }, [user, client]);

  // 2. Set external-account-id cookie if not already present (backwards compat)
  useEffect(() => {
    if (!user.userID) return;
    const cookieExists = document.cookie
      .split("; ")
      .some((c) => c.startsWith(`${EXTERNAL_ACCOUNT_ID_COOKIE_NAME}=`));
    if (!cookieExists) {
      document.cookie = `${EXTERNAL_ACCOUNT_ID_COOKIE_NAME}=${user.userID}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
    }
  }, [user.userID]);

  // 3. Log route impressions
  const { route } = useRoutePathname();
  useImpressionLogger(client, route, user);

  // 4. Open the visibility gate the moment the page is first on screen, so
  //    anything held by `useStatsigClient` while it was parked flushes
  //    (PE-1775). The gate self-heals on send, so this only speeds up the
  //    hidden -> visible transition; it is not what makes the gate correct.
  useOnVisible(markPageVisible);

  // 5. Initialize session replay and autocapture in production, once the page
  //    is actually on screen (PE-1775). Owlet warm-loads Felt pages at launch,
  //    so initializing on mount started a recording — and billed
  //    `session_start` / `page_view` / `performance` / `web_vitals` — for a
  //    screen nobody saw. Rides the same `useOnVisible` gate as `screen_viewed`
  //    and `content_visible`; `hasInitializedExtras` keeps it to once per mount
  //    because the gate fires on every visible tick, not just the first.
  useOnVisible(
    () => {
      if (!client || hasInitializedExtras.current) return;

      const isProduction =
        process.env.NEXT_PUBLIC_STATSIG_TIER === "production" ||
        process.env.VERCEL_TARGET_ENV === "production";

      if (!isProduction) return;

      runStatsigSessionReplay(client);
      runStatsigAutoCapture(client);
      hasInitializedExtras.current = true;
    },
    { watch: [client] }
  );

  return null;
}

export default StatsigObserver;
